<template>
  <div id="app">
    <notification-display-manager :is-inactive="isInactive" />
    <router-view :key="$route.fullPath" />
    <div
      v-if="isStaff && isChatbotEnabled"
      class="chat-panel"
      :class="{
        'chat-open': isChatbotOpen,
        min: isAtMostMD,
        wide: isMD,
        'left-sidebar-registered': leftSidebarRegistered,
        'header-registered': headerRegistered,
      }"
    >
      <Suspense>
        <chatbot />
      </Suspense>
    </div>
  </div>
</template>

<script>
import { inactiveUserTimeThreshold, userActivityThrottlerTime } from '@/app.config';
import { mapGetters } from 'vuex';
import useViewSizes from '@/utils/mixins/useViewSizes';
import { clearChatbotLayoutState, syncChatbotLayoutState } from '@/utils/chatbotLayout';
import NotificationDisplayManager from '@/components/notifications/NotificationDisplayManager.vue';
import Chatbot from '@/components/chatbot/Chatbot.vue';

export default {
  name: 'ClientAdminApp',
  components: {
    NotificationDisplayManager,
    Chatbot,
  },
  mixins: [useViewSizes],
  provide() {
    return {
      registerLeftSidebar: this.registerLeftSidebar,
      registerHeader: this.registerHeader,
    };
  },
  data: () => ({
    registration: null,
    refreshing: false,
    isInactive: false,
    userActivityThrottlerTimeout: null,
    userActivityTimeout: null,
    leftSidebarRegistered: false,
    headerRegistered: false,
  }),
  computed: {
    ...mapGetters('featureFlagStore', ['isChatbotEnabled']),
    ...mapGetters('responsive', ['isMD', 'isAtMostMD']),
    ...mapGetters('chatbot', { isChatbotOpen: 'isOpen', chatbotPanelWidth: 'panelWidth' }),
    ...mapGetters('auth', ['isStaff']),
    chatbotLayoutState() {
      return {
        isOpen: this.isChatbotEnabled && this.isChatbotOpen,
        isDesktop: !this.isAtMostMD,
        width: this.chatbotPanelWidth,
      };
    },
  },
  watch: {
    chatbotLayoutState: {
      handler(layoutState) {
        syncChatbotLayoutState(layoutState);
      },
      immediate: true,
    },
  },
  created() {
    // Listen for swUpdated event and display refresh snackbar as required.
    document.addEventListener('swUpdated', this.showRefreshUI, { once: true });
    // Refresh all open app tabs when a new service worker is installed.
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Don't automatically refresh if the route specifies not to.
        if (this.refreshing || this.$route.meta.refreshOnServiceWorkerControllerChanged === false) {
          return;
        }
        this.refreshing = true;
        window.location.reload();
      });
    }
  },
  mounted() {
    this.$loadScript(process.env.VUE_APP_AUTHORIZE_NET_URL)
      .then(() => {
        // Script is loaded, do something
      })
      .catch(err => {
        console.error(err);
      });
  },
  beforeMount() {
    this.activateActivityTracker();
  },
  beforeUnmount() {
    clearChatbotLayoutState();
    this.deactivateActivityTracker();
    clearTimeout(this.userActivityTimeout);
    clearTimeout(this.userActivityThrottlerTimeout);
  },
  methods: {
    registerLeftSidebar(isRegistered) {
      this.leftSidebarRegistered = isRegistered;
    },
    registerHeader(isRegistered) {
      this.headerRegistered = isRegistered;
    },
    showRefreshUI(e) {
      this.registration = e.detail;

      this.$toasted.show('A new version of Client Admin is available', {
        theme: 'toasted-primary',
        position: 'bottom-right',
        duration: null,
        action: {
          text: 'Refresh',
          onClick: (e, toastObject) => {
            if (!this.registration || !this.registration.waiting) {
              return;
            }
            this.registration.waiting.postMessage('skipWaiting');
            toastObject.goAway(0);
          },
        },
      });
    },
    activateActivityTracker() {
      window.addEventListener('mousemove', this.userActivityThrottler);
      window.addEventListener('scroll', this.userActivityThrottler);
      window.addEventListener('keydown', this.userActivityThrottler);
      window.addEventListener('resize', this.userActivityThrottler);
      document.addEventListener('visibilitychange', this.tabVisibilityChange);
    },

    deactivateActivityTracker() {
      window.removeEventListener('mousemove', this.userActivityThrottler);
      window.removeEventListener('scroll', this.userActivityThrottler);
      window.removeEventListener('keydown', this.userActivityThrottler);
      window.removeEventListener('resize', this.userActivityThrottler);
      document.removeEventListener('visibilitychange', this.tabVisibilityChange);
    },

    tabVisibilityChange() {
      this.isInactive = document.hidden;
    },

    resetUserActivityTimeout() {
      clearTimeout(this.userActivityTimeout);

      this.userActivityTimeout = setTimeout(() => {
        this.userActivityThrottler();
        this.inactiveUserAction();
      }, inactiveUserTimeThreshold);
    },

    userActivityThrottler() {
      if (this.isInactive) {
        this.isInactive = false;
      }

      if (!this.userActivityThrottlerTimeout) {
        this.userActivityThrottlerTimeout = setTimeout(() => {
          this.resetUserActivityTimeout();
          clearTimeout(this.userActivityThrottlerTimeout);
          this.userActivityThrottlerTimeout = null;
        }, userActivityThrottlerTime);
      }
    },

    inactiveUserAction() {
      this.isInactive = true;
    },
  },
};
</script>

<style lang="scss">
@import '~nprogress/nprogress.css';
@import '~@fortawesome/fontawesome-pro/css/all.css';
@import './assets/theme/bundle.scss';

.chat-panel {
  position: fixed;
  right: 0;
  top: 0;
  height: 100vh;
  /* Display over loading masks (z-index: 2000) and under navigation dropdowns (z-index: 2001+) */
  z-index: 2000;

  &.header-registered {
    top: 60px;
    height: calc(100vh - 60px);
  }

  &.min.header-registered {
    top: 72px;
    height: calc(100vh - 72px);

    &.left-sidebar-registered {
      top: 132px;
      height: calc(100vh - 132px);
    }

    &.wide {
      top: 64px;
      height: calc(100vh - 64px);

      &.left-sidebar-registered {
        top: 104px;
        height: calc(100vh - 104px);
      }
    }
  }
}

.el-overlay,
.el-overlay-dialog,
.el-overlay-message-box {
  right: var(--chatbot-panel-width);
  transition: right 400ms cubic-bezier(0.39, 0.71, 0.54, 1.03);
}
</style>
